/**
 * useParentStatus - Hook to detect trial vs client status and feature access
 * 
 * Determines parent/child status based on:
 * - subscription_status on children table
 * - trial_lessons_used / trial_lessons_max
 * - subscriptions table for trial end date
 * - feature_flags JSONB for admin overrides
 * 
 * Feature Flag Semantics:
 * - null/missing = use default logic (trial status, skill progress, etc.)
 * - true = admin override: force enabled
 * - false = admin override: force disabled
 * 
 * Used to enforce restrictions:
 * - Trial users: max 1 free lesson, limited Learn access
 * - Client users: full access
 * - Admin can override any restriction per-child
 */

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { Sentry } from '@/lib/sentry';
import { FEATURES } from '@/lib/feature-flags';

/**
 * Feature flags stored in children.feature_flags JSONB column
 * null = use default logic, true = force enabled, false = force disabled
 */
export interface ChildFeatureFlags {
  learn_enabled?: boolean | null;
  schedule_enabled?: boolean | null;
  voice_agent_enabled?: boolean | null;
  messages_enabled?: boolean | null;
  consonant_enabled?: boolean | null;
}

export interface ParentStatus {
  isLoading: boolean;
  isInTrial: boolean;
  isClient: boolean;
  trialLessonsUsed: number;
  trialLessonsMax: number;
  trialLessonsRemaining: number;
  trialEndsAt: Date | null;
  trialDaysRemaining: number | null;
  trialExpired: boolean;  // NEW: true if trial period has ended
  canScheduleLesson: boolean;
  canAccessLearn: boolean;
  canAccessVoiceAgent: boolean;
  canAccessMessages: boolean;
  canAccessConsonant: boolean;
  hasSkillProgress: boolean;
  subscriptionStatus: string | null;
  featureFlags: ChildFeatureFlags;
  error: string | null;
}

interface UseParentStatusOptions {
  childId?: string | null;
  profileId?: string | null;
}

export function useParentStatus(options: UseParentStatusOptions = {}): ParentStatus {
  const { childId, profileId } = options;
  
  const [status, setStatus] = useState<ParentStatus>({
    isLoading: true,
    isInTrial: false,
    isClient: false,
    trialLessonsUsed: 0,
    trialLessonsMax: 1,
    trialLessonsRemaining: 1,
    trialEndsAt: null,
    trialDaysRemaining: null,
    trialExpired: false,
    canScheduleLesson: true,
    canAccessLearn: true,
    canAccessVoiceAgent: true,
    canAccessMessages: true,
    canAccessConsonant: true,
    hasSkillProgress: false,
    subscriptionStatus: null,
    featureFlags: {},
    error: null,
  });

  const fetchStatus = useCallback(async () => {
    if (!childId && !profileId) {
      setStatus(prev => ({ ...prev, isLoading: false }));
      return;
    }

    // Type for child data from database
    type ChildQueryResult = {
      id: string;
      subscription_status: string | null;
      trial_lessons_used: number | null;
      trial_lessons_max: number | null;
      feature_flags: unknown;
    };

    try {
      // Build query based on what we have
      let childData: ChildQueryResult | null = null;

      if (childId) {
        // Direct child lookup
        const { data, error } = await supabase
          .from('children')
          .select('id, subscription_status, trial_lessons_used, trial_lessons_max, feature_flags')
          .eq('id', childId)
          .maybeSingle();
        
        if (error) throw error;
        childData = data as ChildQueryResult | null;
      } else if (profileId) {
        // Get first child for this parent
        const { data, error } = await supabase
          .from('children')
          .select('id, subscription_status, trial_lessons_used, trial_lessons_max, feature_flags')
          .eq('parent_id', profileId)
          .order('created_at', { ascending: true })
          .limit(1)
          .maybeSingle();
        
        if (error) throw error;
        childData = data as ChildQueryResult | null;
      }

      if (!childData) {
        setStatus(prev => ({
          ...prev,
          isLoading: false,
          error: null,
        }));
        return;
      }

      // Check for skill progress (determines Learn access)
      const { data: skillProgress } = await supabase
        .from('child_skill_progress')
        .select('id')
        .eq('child_id', childData.id)
        .limit(1);

      // When SKILL_PROGRESSION is off, don't block Learn access based on empty progress
      const hasSkillProgress = !FEATURES.SKILL_PROGRESSION || (skillProgress?.length ?? 0) > 0;

      // Check subscription for trial end date
      let trialEndsAt: Date | null = null;
      let trialDaysRemaining: number | null = null;

      const { data: subscription } = await supabase
        .from('subscriptions')
        .select('trial_end, status')
        .eq('child_id', childData.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (subscription?.trial_end) {
        trialEndsAt = new Date(subscription.trial_end);
        const now = new Date();
        const diffMs = trialEndsAt.getTime() - now.getTime();
        trialDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
      }

      // Determine status
      const subscriptionStatus = childData.subscription_status;
      const isInTrial = subscriptionStatus === 'trialing';
      const isClient = subscriptionStatus === 'active';
      
      const trialLessonsUsed = childData.trial_lessons_used ?? 0;
      const trialLessonsMax = childData.trial_lessons_max ?? 1;
      const trialLessonsRemaining = Math.max(0, trialLessonsMax - trialLessonsUsed);
      
      // Feature flags from admin (null = use default logic)
      const featureFlags: ChildFeatureFlags = (childData.feature_flags as ChildFeatureFlags) ?? {};

      // Check if trial has expired (time-based, not just lesson-based)
      const trialExpired = isInTrial && trialEndsAt !== null && new Date() > trialEndsAt;

      // Determine permissions with admin override support
      // Admin override: true = force enabled, false = force disabled, null/undefined = default logic
      
      // Schedule access: default = client OR (trial with lessons remaining AND not expired)
      // Both conditions must be met: lessons available AND trial period not expired
      const defaultCanSchedule = isClient || (isInTrial && !trialExpired && trialLessonsRemaining > 0);
      const canScheduleLesson = featureFlags.schedule_enabled ?? defaultCanSchedule;
      
      // Learn/Pages access: always available — parents need to request books before first lesson
      const canAccessLearn = featureFlags.learn_enabled ?? true;
      
      // Voice agent access: default = true (always available)
      const canAccessVoiceAgent = featureFlags.voice_agent_enabled ?? true;
      
      // Messages access: default = true (always available)
      const canAccessMessages = featureFlags.messages_enabled ?? true;

      // Consonant practice access: default = true (always available)
      const canAccessConsonant = featureFlags.consonant_enabled ?? true;

      Sentry.setContext('parent_status', {
        childId: childData.id,
        subscriptionStatus,
        isInTrial,
        isClient,
        trialExpired,
        canScheduleLesson,
        canAccessLearn,
        canAccessVoiceAgent,
        canAccessMessages,
        canAccessConsonant,
        hasSkillProgress,
        featureFlags: JSON.stringify(featureFlags),
      });

      setStatus({
        isLoading: false,
        isInTrial,
        isClient,
        trialLessonsUsed,
        trialLessonsMax,
        trialLessonsRemaining,
        trialEndsAt,
        trialDaysRemaining,
        trialExpired,
        canScheduleLesson,
        canAccessLearn,
        canAccessVoiceAgent,
        canAccessMessages,
        canAccessConsonant,
        hasSkillProgress,
        subscriptionStatus,
        featureFlags,
        error: null,
      });

    } catch (err) {
      console.error('Error fetching parent status:', err);
      setStatus(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load status',
      }));
    }
  }, [childId, profileId]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  return status;
}

export default useParentStatus;
